import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { memo, useState } from 'react';
import DetailedAlerts from '../AlertDetails/DetailedAlerts';
import DoneIcon from '@mui/icons-material/Done';
import InputBase from '@mui/material/InputBase';
import EditIcon from '@mui/icons-material/Edit';
import { createComments } from 'service/unifiedAlerts';
import CircularProgress from '@mui/material/CircularProgress';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import theme from 'assets/theme';

interface AzureAlert {
  alertId: string;
  mappedSeverity: string;
  severity: string;
  subscriptionName: string;
  monitorCondition: string;
  targetResourceType: string;
  targetResourceGroup: string;
  monitoringService: string;
  alertRule: string;
  alertRuleID: string;
  description?: string;
  signalType?: string;
  firedDateTime: string;
  comments?: string;
}

interface AzureAlertsTableProps {
  alerts: AzureAlert[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSorting: (sortInfo: { sortBy: string; sortDir: string }) => void;
  sortingInfo: { sortBy: string; sortDir: string };
  isLoading: boolean;
  title: string | undefined;
}

const SeverityChip = ({ row }: { row: AzureAlert }) => {
  const val = row.mappedSeverity;

  const colorMap: Record<string, { bg: string; color: string }> = {
    Critical: { bg: '#f54b3c', color: '#fff' },
    Major: { bg: '#f79227', color: '#fff' },
    Minor: { bg: '#f1aa2c', color: '#fff' },
    Warning: { bg: '#facc32', color: '#fff' },
    OK: { bg: '#37b66e', color: '#fff' },
  };

  const style = colorMap[row.mappedSeverity] ?? { bg: '#9E9E9E', color: '#fff' };
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '70px',
        justifyContent: 'center',
      }}
    >
      {val}
    </Box>
  );
};

const defaultColumns = ['ALERT ID', 'ALERT RULE', 'DESCRIPTION', 'ALERT STATUS', 'SEVERITY', 'FIRED DATE TIME', 'SUBSCRIPTION', 'ALERT TYPE', 'AGE', 'COMMENTS', 'ACTIVITY', 'ACTIONS'];

const renderSeverityFormatter = ({ row }: { row: AzureAlert }) => <SeverityChip row={row} />;

const renderTimeFormatter = ({ row }: { row: AzureAlert }) => {
  const d = new Date(row.firedDateTime);
  return (
    <span>
      {Number.isNaN(d.getTime())
        ? row.firedDateTime
        : d.toLocaleString('en-US')}
    </span>
  );
};

const renderAlertIDFormatter = ({ row }: { row: AzureAlert }) => {
  const splitItems = row.alertId?.split('/')
  const id = splitItems.at(-1)
  return (
    <Typography
      variant='tableRowFont'
      sx={{
        color: "blue",
        cursor: "pointer",
        textDecoration: "underline",
      }}
      onClick={() => window.open(`https://portal.azure.com/#resource/${row.alertId}`, "_blank")}
    >
      {id}
    </Typography>)
};

const renderAlertRuleIDFormatter = ({ row }: { row: AzureAlert }) => {
  const splitItems = row.alertRuleID?.split('/')
  const id = splitItems.length > 2 ? splitItems[2] : ''
  return (
    <Typography
      variant='tableRowFont'
      sx={{
        color: "blue",
        cursor: "pointer",
        textDecoration: "underline",
      }}
      onClick={() => window.open(`https://portal.azure.com/#resource/${row.alertRuleID}`, "_blank")}
    >
      {id}
    </Typography>)
};

const AlertsTable = memo(({
  alerts,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSorting,
  sortingInfo,
  isLoading,
  title,
}: AzureAlertsTableProps) => {

  const [isAlertsDrawerOpen, setIsAlertsDrawerOpen] = useState(false);
  const [entireRow, setEntireRow] = useState<AzureAlert | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const p = theme.palette;
  const [statusMap, setStatusMap] = useState<
    Record<string, 'idle' | 'loading' | 'success' | 'error'>
  >({});

  const handleAlertsClick = ({ row }: { row: AzureAlert }) => {
    const handleViewDetailsClick = (row: AzureAlert) => {
      row.severity = row.mappedSeverity
      setEntireRow(row);
      setIsAlertsDrawerOpen(true);
    };
    return (
      <Typography
        variant='tableRowFont'
        sx={{
          color: "dodgerblue !important",
          cursor: "pointer",
        }}
        onClick={() => handleViewDetailsClick(row)}
      >
        VIEW DETAILS
      </Typography>
    );
  };


  const handleCommentChange = (alertId: string, value: string) => {
    setComments((prev) => ({
      ...prev,
      [alertId]: value,
    }));
  };

  const handleCommentSubmit = async (row: AzureAlert) => {
    const comment = comments[row.alertId] ?? '';

    const payload = {
      alertId: row.alertId,
      comments: comment,
    };

    // 👉 set loading
    setStatusMap((prev) => ({
      ...prev,
      [row.alertId]: 'loading',
    }));

    try {
      const resp = await createComments(payload);

      if (resp === 'Comments Added Successfully') {
        setStatusMap((prev) => ({
          ...prev,
          [row.alertId]: 'success',
        }));
      } else {
        throw new Error('Failed');
      }
    } catch (e) {
      console.info(JSON.stringify(e))
      setStatusMap((prev) => ({
        ...prev,
        [row.alertId]: 'error',
      }));
    }

    // 👉 reset after 2 sec
    setTimeout(() => {
      setStatusMap((prev) => ({
        ...prev,
        [row.alertId]: 'idle',
      }));
    }, 2000);

    setEditingRow(null);
  };

  const renderCommentFormatter = ({ row }: { row: AzureAlert }) => {
    const value = comments[row.alertId] ?? row.comments ?? '';
    const isEditing = editingRow === row.alertId;
    const status = statusMap[row.alertId] || 'idle';

    let actionElement: React.ReactNode;

    if (status === 'loading') {
      actionElement = <CircularProgress size={16} />;
    } else if (status === 'success') {
      actionElement = <Box sx={{ fontSize: '14px' }}>✅</Box>;
    } else if (status === 'error') {
      actionElement = <Box sx={{ fontSize: '14px' }}>❌</Box>;
    } else if (isEditing) {
      actionElement = (
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault(); // important to prevent blur
            handleCommentSubmit(row);
          }}
          sx={{ p: 0.5 }}
        >
          <DoneIcon fontSize="small" />
        </IconButton>
      );
    } else {
      actionElement = (
        <IconButton
          size="small"
          onClick={() => setEditingRow(row.alertId)}
          sx={{ p: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      );
    }

    return (
      <Box
        sx={{
          width: 220,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
        onClick={() => !isEditing && setEditingRow(row.alertId)}
      >
        <InputBase
          value={value}
          readOnly={!isEditing}
          onChange={(e) => handleCommentChange(row.alertId, e.target.value)}
          onBlur={() => {
            if (isEditing) {
              setEditingRow(null);
            }
          }}
          sx={{
            flex: 1,
            fontSize: '0.85rem',
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: isEditing ? '#f5f7fb' : 'transparent',
            '& .MuiInputBase-input': {
              color: '#000',
              padding: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#6b7280',
              opacity: 1,
            },
          }}
        />
        {actionElement}
      </Box>
    );
  };

  const renderAgeFormatter = ({ row }: { row: AzureAlert }) => {
    const start = row?.firedDateTime;

    if (!start) return '-';

    const startTime = new Date(start).getTime();
    const endTime = new Date().getTime();

    const diffMs = endTime - startTime;

    if (isNaN(diffMs) || diffMs < 0) return '-';

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    // Format nicely
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const renderButtons = ({ row }: { row: AzureAlert }) => {


    const playActionElement = (

      <IconButton
        size="small"
        sx={{ p: 0.5 }}
      >
        <Tooltip title={'Self Heal'} arrow>
          <PlayArrowRoundedIcon fontSize="small" />
        </Tooltip>
      </IconButton>
    );

    const descriptionActionElement = (
      <IconButton
        size="small"
        sx={{ p: 0.5 }}
      >
        <Tooltip title={'SOP'} arrow>
          <DescriptionRoundedIcon fontSize="small" />
        </Tooltip>
      </IconButton>
    );

    return (
      <Box
        sx={{
          width: 220,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {playActionElement}
        {descriptionActionElement}
      </Box>
    );
  };

  const columns = [
    { key: 'alertId', name: 'ALERT ID', width: 150, formatter: renderAlertIDFormatter },
    { key: 'alertRule', name: 'ALERT RULE', width: 150 },
    {
      key: 'description', name: 'DESCRIPTION', width: 200,
      formatter: ({ row }: { row: AzureAlert }) => {
        const description = row.description ?? '';
        return (
          <span title={description}>
            {description.length > 50 ? `${description.slice(0, 50)}...` : description}
          </span>
        );
      }
    },
    { key: 'monitorCondition', name: 'ALERT STATUS', width: 110 },
    {
      key: 'mappedSeverity',
      name: 'SEVERITY',
      width: 110,
      renderCell: SeverityChip,
      formatter: renderSeverityFormatter,
    },
    { key: 'firedDateTime', name: 'FIRED DATE TIME', width: 180, formatter: renderTimeFormatter },
    { key: 'subscriptionName', name: 'SUBSCRIPTION', width: 200 },
    { key: 'signalType', name: 'ALERT TYPE', width: 110 },
    {
      key: 'age',
      name: 'AGE',
      width: 120,
      formatter: renderAgeFormatter,
    },
    { key: 'targetResourceType', name: 'TARGET RESOURCE TYPE', width: 200 },
    { key: 'targetResourceGroup', name: 'TARGET RESOURCE GROUP', width: 150 },
    { key: 'monitoringService', name: 'MONITORING SERVICE', width: 110 },
    { key: 'alertRuleID', name: 'ALERT RULE ID', width: 350, formatter: renderAlertRuleIDFormatter },
    {
      key: 'comments',
      name: 'COMMENTS',
      width: 240,
      formatter: renderCommentFormatter,
    },
    {
      key: 'newButton',
      name: 'ACTIVITY',
      width: 100,
      formatter: renderButtons,
    },
    { key: 'actions', name: 'ACTIONS', width: 150, formatter: handleAlertsClick },
  ];

  return (
    <Box
      sx={{
        '& .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
          paddingTop: '16px !important',
          paddingBottom: '16px !important',
        },
        '& .rdg-header-row .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
        },
        '& .rdg-row': {
          borderBlockEnd: '1px solid #fff !important',
        },
      }}
    >

      <Typography variant="h4" sx={{ color: p.primary.main, fontWeight: 500, position: 'absolute' }}>
        {title}
      </Typography>

      <CustomizedDataGrid
        key="azure-unified-alerts-grid"
        tableColumns={columns}
        tableData={alerts}
        tableType="UnifiedAlertsAzure"
        defaultColumnNames={defaultColumns}
        listLength={total}
        page={page}
        pageSize={pageSize}
        paginationData={{ totalElements: total, totalPages: Math.ceil(total / pageSize) }}
        onSorting={onSorting}
        currSorting={sortingInfo}
        handlePageChange={(_: any, p: number) => onPageChange(p)}
        handlepageSizeChange={(e: any) => onPageSizeChange(Number.parseInt(e.target.value))}
        isFetching={isLoading}
        isDateRangeFilter={false}
        isDisabled={false}
        isShowMoreButton={true}
        pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
        isSortIcon={true}
      />

      <DetailedAlerts
        open={isAlertsDrawerOpen}
        onClose={() => setIsAlertsDrawerOpen(false)}
        onOpen={() => setIsAlertsDrawerOpen(true)}
        entireRow={entireRow}
      />
    </Box>
  );
});

export default AlertsTable;